import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    
    // Proactive Lookahead Preloader for Mobile / Instagram WebViews
    this.addEventListener('touchstart', this.#handlePreloadNextSlides, { passive: true, signal });
    this.addEventListener('pointerdown', this.#handlePreloadNextSlides, { passive: true, signal });
    this.slideshow?.addEventListener('change', this.#handleSlideChangePreload, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }


  #preloadedIndices = new Set([0, 1]);

  #handlePreloadNextSlides = () => {
    // Preload next 2 upcoming slides on first user touch/interaction
    const currentIndex = this.slideshow?.currentIndex || 0;
    this.#preloadSlideImages(currentIndex + 1);
    this.#preloadSlideImages(currentIndex + 2);
  };

  #handleSlideChangePreload = (event) => {
    const nextIndex = (event?.detail?.index ?? this.slideshow?.currentIndex ?? 0) + 1;
    this.#preloadSlideImages(nextIndex);
    this.#preloadSlideImages(nextIndex + 1);
  };

  #preloadSlideImages = (index) => {
    if (index < 0 || this.#preloadedIndices.has(index)) return;
    const slides = this.querySelectorAll('slideshow-slide, .product-media-container');
    if (slides && slides[index]) {
      const img = slides[index].querySelector('img[loading="lazy"]');
      if (img) {
        img.loading = 'eager';
        if (img.dataset.src && !img.src) img.src = img.dataset.src;
        this.#preloadedIndices.add(index);
      }
    }
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
